import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { clearReturnPath, peekReturnPath } from '../lib/session/returnPath';
import { type SessionPermission, useSession } from '../lib/session/sessionContext';
import { PermissionProvider } from '../ui/permissions/PermissionProvider';
import { toast } from '../ui/shadcn/sonner';
import { BootstrapGate } from './BootstrapGate';
import { sessionPermissionResolver } from './sessionPermissionResolver';

/**
 * The no-flash authenticated route guard (F5-a NFR-2 / AC-15) — a pathless layout route wrapping the
 * whole authenticated tree in `router.tsx`. It reads the session the root `SessionProvider` is
 * resolving and renders exactly one of three things:
 *
 *   - `loading` (`/me` unresolved) → the neutral `BootstrapGate`. NOTHING authenticated is in the
 *     DOM until `/me` returns, so there is no flash of authenticated UI on cold load.
 *   - `unauthenticated` (`/me` 401, or a later 401 flipped the store) → a redirect to `/login`,
 *     carrying the attempted location in router `state.from` so F5-a WI-1.2's post-login step can return
 *     the user there (the T2 half of F5-a AC-8; the land-back round-trip is F5-a WI-1.4's browser smoke).
 *   - `authenticated` → the authenticated `<Outlet/>`, wrapped in the real `PermissionProvider` fed
 *     from the `/me` grants (F5-a AC-15), which REPLACES FS-3's allow-everything stub at the app root.
 *
 * `/login` is a sibling route OUTSIDE this gate (added by F5-a WI-1.2), so an unauthenticated user can
 * reach it without being redirected in a loop.
 */
/** The grants of a session that is not authenticated — one stable value, so the memo below holds. */
const NO_PERMISSIONS: readonly SessionPermission[] = [];

export function AuthenticatedGate() {
    const session = useSession();
    const location = useLocation();
    const authenticated = session.status === 'authenticated';

    // BUG-054: the route kept across the Microsoft redirect (`startMicrosoftSignIn`). Router state does
    // not survive leaving the page, and the backend returns the browser to the SPA root, so once `/me`
    // has confirmed the session the kept route is followed here, once: read during render (a pure
    // read), cleared by the effect in the same commit, so the next render finds nothing. On the stub
    // path nothing is kept and LoginRoute's router-state redirect does the work, as before.
    const returnTo = authenticated ? peekReturnPath() : undefined;
    useEffect(() => {
        if (authenticated) {
            clearReturnPath();
        }
    }, [authenticated]);

    // One resolver per set of grants, not per render: `PermissionProvider` publishes it through
    // context, so a new function on every render of the gate re-rendered every permission consumer
    // below it. The grants array is replaced only when `/me` is read again. Above the early returns,
    // because a hook may not sit behind a condition.
    const permissions = session.status === 'authenticated' ? session.permissions : NO_PERMISSIONS;
    const resolve = useMemo(() => sessionPermissionResolver(permissions), [permissions]);

    // Sonner keeps its toasts in module state, outside the tree keyed below, so a toast naming a record
    // ("Saved 2 changes to …") would outlive a sign-out or a change of user. Each session's toasts go
    // with it (L-099).
    const userId = session.status === 'authenticated' ? session.user.id : undefined;
    useEffect(() => {
        if (userId === undefined) {
            return;
        }
        // Runs when this user's session ends: a sign-out, an expiry, another user, or the gate unmounting.
        return () => {
            toast.dismiss();
        };
    }, [userId]);

    if (session.status === 'loading') {
        return <BootstrapGate />;
    }

    if (session.status === 'unauthenticated') {
        // `replace`, so the un-authorised URL does not sit in history behind the login page.
        // Preserve the attempted route for deep-linking (a typed protected URL, or a mid-session 401
        // expiry) so re-auth lands back on it — EXCEPT after a voluntary sign-out (`reason: 'signout'`),
        // which starts a fresh session and should land on the default section, not the page signed out
        // from (BUG-005). Dropping `from` makes LoginRoute fall back to `/` → the first accessible section.
        const state = session.reason === 'signout' ? undefined : { from: location };
        return <Navigate to="/login" replace state={state} />;
    }

    if (returnTo !== undefined && returnTo !== `${location.pathname}${location.search}${location.hash}`) {
        return <Navigate to={returnTo} replace />;
    }

    // Keyed by the user, so a session that changes user while the app is on screen (a `/me` re-read
    // after another tab signed in as someone else) remounts every screen below. A mounted query keeps
    // its last result after `SessionProvider` clears the cache, until it re-renders; a remount reads
    // afresh for the new user (BUG-085).
    return (
        <PermissionProvider key={session.user.id} resolve={resolve}>
            <Outlet />
        </PermissionProvider>
    );
}
